import type { MdtPermission } from "@hydeck/mdt-contracts";
import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { hasCertification, hasPermission } from "../core/session";
import type { ViewName } from "../core/views";
import { Brand } from "./Brand";
import { DumpIcon } from "./DumpIcon";
import type { DumpIconName } from "../generated/dump-icons";

type NavItem = {
  name: ViewName;
  icon: DumpIconName;
  permission?: MdtPermission;
  certification?: string;
};

const navItems: NavItem[] = [
  { name: "Dashboard", icon: "nav_dashboard" },
  { name: "Bookmarks", icon: "nav_stars", permission: "bookmarks.view" },
  { name: "Profiles", icon: "nav_profiles", permission: "citizens.view" },
  { name: "Incidents", icon: "nav_incidents", permission: "incidents.view" },
  { name: "Reports", icon: "nav_reports", permission: "reports.view" },
  { name: "Evidence", icon: "nav_evidence", permission: "evidence.view" },
  { name: "Vehicles", icon: "nav_reports", permission: "vehicles.view" },
  { name: "Properties", icon: "nav_properties", permission: "properties.view" },
  { name: "Charges", icon: "nav_charges", permission: "charges.view" },
  { name: "Staff", icon: "nav_staff", permission: "users.view" },
  { name: "FTO Reports", icon: "nav_staff", certification: "FTO" },
  { name: "Legislation", icon: "nav_legislation" },
  { name: "Calendar", icon: "nav_calendar" },
  { name: "Shared Reports", icon: "nav_reports" },
  { name: "Docket", icon: "nav_docket", permission: "dockets.view" },
];

type SidebarProps = {
  activeView: ViewName;
  onSelect: (name: ViewName) => void;
};

export function Sidebar(props: SidebarProps) {
  const [clock, setClock] = createSignal(new Date());
  const available = (item: NavItem) =>
    hasPermission(item.permission) && hasCertification(item.certification);

  onMount(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    onCleanup(() => window.clearInterval(timer));
  });

  return (
    <aside class="relative flex h-32 w-full flex-col gap-8 border-r border-[rgba(255,255,255,.08)] pr-6 lg:h-full lg:w-[18rem]">
      <div class="flex items-center justify-between">
        <Brand />
        <select
          class="block h-8 w-1/3 bg-[rgba(255,255,255,.11)] px-3 text-left disabled:cursor-not-allowed disabled:opacity-40 lg:hidden"
          value={props.activeView}
          onChange={(event) =>
            props.onSelect(event.currentTarget.value as ViewName)
          }
        >
          <For each={navItems.filter(available)}>
            {(item) => (
              <option class="text-black-54" value={item.name}>
                {item.name}
              </option>
            )}
          </For>
        </select>
      </div>

      <ul
        class="nav-list hidden flex-col gap-2 lg:flex"
        aria-label="MDT sections"
      >
        <For each={navItems}>
          {(item) => (
            <Show when={available(item)}>
              <li>
                <button
                  class={`group flex h-10 w-full cursor-pointer items-center gap-4 rounded border-[1px] pl-4 hover:border-cyan ${
                    item.name === props.activeView
                      ? "active-path-cyan border-cyan"
                      : "border-[transparent]"
                  }`}
                  onClick={() => props.onSelect(item.name)}
                >
                  <DumpIcon name={item.icon} />
                  <span
                    class="pt-[0.1rem] group-hover:text-cyan"
                    classList={{ "text-cyan": item.name === props.activeView }}
                  >
                    {item.name}
                  </span>
                </button>
              </li>
            </Show>
          )}
        </For>
      </ul>

      <Show when={hasPermission("config.view")}>
        <ul class="nav-list mt-auto hidden flex-col gap-2 lg:flex">
          <li>
            <button
              class={`group flex h-10 w-full cursor-pointer items-center gap-4 rounded border-[1px] pl-4 text-white-55 hover:border-cyan ${
                props.activeView === "Config"
                  ? "active-path-cyan border-cyan"
                  : "border-[transparent]"
              }`}
              onClick={() => props.onSelect("Config")}
            >
              <DumpIcon name="nav_config" />
              <span class="group-hover:text-cyan">Config</span>
            </button>
          </li>
        </ul>
      </Show>

      <p class="hidden pl-4 text-xs text-white-55 lg:block">
        Current Time:
        <br />
        {clock().toUTCString()}
      </p>
    </aside>
  );
}
